import { IAwakenListener } from "./sleepModeDetector";
import { IChannelTransport } from "./types";

export interface ISocketLogger {
    log(s: string): void;
    warn(s: string): void;
    error(s: string): void;
}

export function webSocket(
    host: string,
    sleepModeDetector?: IAwakenListener | null,
    logger?: ISocketLogger
): IChannelTransport {
    let isConnected = false,
        socket: WebSocket | null,
        onDeviceAwaken = function () {
            if (socket) {
                logger?.warn("It seems like device was asleep.");
                socket.send("PING");
            }
        },
        onSocketClosed = function () {
            if (isConnected) {
                isConnected = false;
                socket && socket.close();
                sleepModeDetector &&
                    sleepModeDetector.unsubscribe(onDeviceAwaken);
                logger?.warn("Closing websocket.");
            }
        },
        setupSocketConnection = function (
            database: string,
            channelId: string,
            onConnect: (e: Event) => void,
            onMessage: (data: any) => void,
            onError: (error: Error) => void,
            onDisconnect: (event: CloseEvent) => void
        ) {
            const wsUrl = `wss://${host}/${database}/?channelId=${channelId}`;
            const onSocketMessage = function (messageEvent: MessageEvent) {
                try {
                    onMessage(JSON.parse(messageEvent.data));
                } catch (e) {
                    onError(e as Error);
                }
            };
            const onSocketError = function (_: Event) {
                // unfortunately Event doesn't have any information regarding error
                onError(new Error(`WebSocket error`));
                onSocketClosed();
            };
            const onSocketClose = function (e: CloseEvent) {
                onDisconnect(e);
                onSocketClosed();
            };

            let localSocket: WebSocket | null = null;

            try {
                localSocket = new WebSocket(wsUrl);
            } catch (e) {
                onError(
                    new Error(
                        `${(e as Error).message}. WebSocket threw an error.`
                    )
                );
            }

            if (localSocket) {
                isConnected = true;

                localSocket.onopen = onConnect;
                localSocket.onmessage = onSocketMessage;
                localSocket.onerror = onSocketError;
                localSocket.onclose = onSocketClose;

                sleepModeDetector && sleepModeDetector.awaken(onDeviceAwaken);
            }

            return localSocket;
        };

    return {
        listen: function (
            database: string,
            channelId: string,
            onConnect: (e: Event) => void,
            onMessage: (data: any) => void,
            onError: (error: Error) => void,
            onDisconnect: (event: CloseEvent) => void
        ) {
            if (!isConnected) {
                socket = setupSocketConnection(
                    database,
                    channelId,
                    onConnect,
                    onMessage,
                    onError,
                    onDisconnect
                );
            } else {
                logger?.warn("websocket is already opened.");
            }
        },
        disconnect: function () {
            if (socket && isConnected) {
                socket.close();
                socket = null;
                onSocketClosed();
            }
        },
        isConnected: () => isConnected,
        send: function (message: string) {
            if (socket && isConnected) {
                socket.send(message);
            } else {
                logger?.error("Cannot send message: WebSocket is not connected");
            }
        },
    };
}

